import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon, XMarkIcon, CheckIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { GameOutput } from '../../../generated-api';
import { RecentGame } from '../../../hooks/useRecentGames';
import GameCard from '../GameCard';

interface GameSelectionStepProps {
  recentGames: RecentGame[];
  recentGamesLoading: boolean;
  allGames: GameOutput[];
  selectedGameId: string;
  selectedGame: GameOutput | null;
  gameExtensions: string[];
  onSelectGame: (gameId: string) => void;
  onToggleExtension: (extension: string) => void;
}

const GameSelectionStep: React.FC<GameSelectionStepProps> = ({
  recentGames,
  recentGamesLoading,
  allGames,
  selectedGameId,
  selectedGame,
  gameExtensions,
  onSelectGame,
  onToggleExtension,
}) => {
  const [showSearch, setShowSearch] = useState(recentGames.length === 0 && !recentGamesLoading);
  const [searchQuery, setSearchQuery] = useState('');
  const [playerCountFilter, setPlayerCountFilter] = useState<string>('ALL');
  const [ownerFilter, setOwnerFilter] = useState<string>('ALL');
  const [tagFilter, setTagFilter] = useState<string>('ALL');
  const [showFilters, setShowFilters] = useState(false);

  // Derive filter options from game library
  const filterOptions = useMemo(() => {
    const playerCounts = new Set<number>();
    const owners = new Set<string>();
    const tags = new Set<string>();
    for (const game of allGames) {
      for (let i = game.minTeams; i <= game.maxTeams; i++) playerCounts.add(i);
      game.owners?.forEach(o => owners.add(o));
      game.tags?.forEach(t => tags.add(t));
    }
    return {
      playerCounts: Array.from(playerCounts).sort((a, b) => a - b),
      owners: Array.from(owners).sort(),
      tags: Array.from(tags).sort(),
    };
  }, [allGames]);

  const hasActiveFilters = playerCountFilter !== 'ALL' || ownerFilter !== 'ALL' || tagFilter !== 'ALL';

  const filteredGames = allGames.filter(game => {
    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch = game.name.toLowerCase().includes(q);
      const tagMatch = game.tags?.some(t => t.toLowerCase().includes(q));
      if (!nameMatch && !tagMatch) return false;
    }
    // Player count filter
    if (playerCountFilter !== 'ALL') {
      const count = parseInt(playerCountFilter);
      if (count < game.minTeams || count > game.maxTeams) return false;
    }
    // Owner filter
    if (ownerFilter !== 'ALL') {
      if (!game.owners?.includes(ownerFilter)) return false;
    }
    // Tag filter
    if (tagFilter !== 'ALL') {
      if (!game.tags?.includes(tagFilter)) return false;
    }
    return true;
  });

  const handleSelectFromSearch = (gameId: string) => {
    onSelectGame(gameId);
    setShowSearch(false);
    setSearchQuery('');
    resetFilters();
  };

  const resetFilters = () => {
    setPlayerCountFilter('ALL');
    setOwnerFilter('ALL');
    setTagFilter('ALL');
    setShowFilters(false);
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
    resetFilters();
  };

  // Full-screen game search overlay — rendered via portal to escape transform containment
  const searchOverlay = showSearch ? createPortal(
    <div className="fixed inset-0 z-[70] bg-surface flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-edge bg-surface shrink-0">
        <h2 className="text-lg font-semibold text-heading">Select Game</h2>
        <button
          type="button"
          onClick={handleCloseSearch}
          className="p-2 text-disabled hover:text-body rounded-full hover:bg-surface-active min-h-[48px] min-w-[48px] flex items-center justify-center"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="p-4 border-b border-edge bg-surface shrink-0">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-disabled" />
            </div>
            <input
              type="text"
              placeholder="Search games..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-edge-input rounded-lg
                         focus:ring-primary-500 focus:border-primary-500 text-base bg-surface"
              autoFocus
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`relative shrink-0 w-12 h-12 flex items-center justify-center rounded-lg border transition-colors
                        ${hasActiveFilters
                          ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-600'
                          : 'bg-surface border-edge-input text-body hover:bg-surface-hover'
                        }`}
            aria-label="Toggle filters"
            aria-expanded={showFilters}
          >
            <FunnelIcon className="h-5 w-5" />
            {hasActiveFilters && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-primary-500" />
            )}
          </button>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mt-3 space-y-3">
            <div>
              <label htmlFor="wizardPlayerCountFilter" className="block text-sm font-medium text-body mb-1">
                Player Count
              </label>
              <select
                id="wizardPlayerCountFilter"
                value={playerCountFilter}
                onChange={(e) => setPlayerCountFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500 bg-surface"
              >
                <option value="ALL">All Player Counts</option>
                {filterOptions.playerCounts.map(count => (
                  <option key={count} value={count.toString()}>{count} players</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="wizardOwnerFilter" className="block text-sm font-medium text-body mb-1">
                Owner
              </label>
              <select
                id="wizardOwnerFilter"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500 bg-surface"
              >
                <option value="ALL">All Owners</option>
                {filterOptions.owners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="wizardTagFilter" className="block text-sm font-medium text-body mb-1">
                Tag
              </label>
              <select
                id="wizardTagFilter"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500 bg-surface"
              >
                <option value="ALL">All Tags</option>
                {filterOptions.tags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <div className="text-sm text-body">
                Showing {filteredGames.length} of {allGames.length} games
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-surface-base">
        {filteredGames.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredGames.map(game => (
              <GameCard
                key={game.id}
                game={game}
                isSelected={selectedGameId === game.id}
                onSelect={handleSelectFromSearch}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-heading mb-2">No games found</h3>
            <p className="text-muted text-sm">
              {searchQuery || hasActiveFilters
                ? 'Try adjusting your search or filters'
                : 'No games in your library yet'}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative flex flex-col h-full">
      {searchOverlay}

      {/* Game grid — scrollable area with bottom padding for floating button */}
      <div className="flex-1 overflow-y-auto pb-4 p-1 -m-1">
        {/* Recent games grid */}
        {recentGamesLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="animate-pulse rounded-xl bg-surface-skeleton aspect-square" />
            ))}
          </div>
        ) : recentGames.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Show search-selected game first if not in recents */}
            {selectedGame && selectedGameId && !recentGames.find(rg => rg.game.id === selectedGameId) && (
              <GameCard
                key={selectedGame.id}
                game={selectedGame}
                isSelected
                onSelect={onSelectGame}
              />
            )}
            {recentGames.slice(0, 6).map(rg => (
              <GameCard
                key={rg.game.id}
                game={rg.game}
                isSelected={selectedGameId === rg.game.id}
                onSelect={onSelectGame}
                lastPlayedAt={rg.lastPlayedAt}
              />
            ))}
          </div>
        ) : selectedGame && selectedGameId ? (
          <div className="grid grid-cols-2 gap-3">
            <GameCard
              game={selectedGame}
              isSelected
              onSelect={onSelectGame}
            />
          </div>
        ) : null}

        {/* Extensions (if selected game has them) */}
        {selectedGame?.availableExtensions && selectedGame.availableExtensions.length > 0 && (
          <div className="mt-4">
            <span className="text-xs font-medium text-muted block mb-2">Extensions</span>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {selectedGame.availableExtensions.map(ext => {
                const isActive = gameExtensions.includes(ext);
                return (
                  <button
                    data-track="wizard_extension"
                    key={ext}
                    type="button"
                    onClick={() => onToggleExtension(ext)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium
                                border transition-all duration-150
                                active:scale-[0.95] motion-reduce:active:scale-100
                                ${isActive
                                  ? 'bg-accent-500 text-white border-accent-500'
                                  : 'bg-surface border-edge text-body hover:border-edge-input'
                                }`}
                    aria-pressed={isActive}
                  >
                    {isActive && <CheckIcon className="w-3 h-3 inline mr-1" strokeWidth={2.5} />}
                    {ext}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Floating search button */}
      <div className="absolute -bottom-12 inset-x-0 p-3 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base)] to-transparent pt-6">
        <button
          type="button"
          onClick={() => setShowSearch(true)}
          className="w-full flex items-center justify-center gap-2 py-3 px-4
                     rounded-lg border border-edge bg-surface shadow-sm
                     text-sm font-medium text-muted
                     hover:border-primary-400 hover:text-primary-700
                     hover:bg-primary-50 dark:hover:bg-primary-900/20
                     transition-all duration-150
                     active:scale-[0.98] motion-reduce:active:scale-100
                     min-h-[48px]"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
          Search all games...
        </button>
      </div>
    </div>
  );
};

export default GameSelectionStep;
